import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { savingsService } from '../../services/savingsService'

function SimpananBungaHarian() {
  const [accounts, setAccounts] = useState([])
  const [transactions, setTransactions] = useState([])
  const [interestRates, setInterestRates] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('accounts')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [showClosingModal, setShowClosingModal] = useState(false)
  const [closing, setClosing] = useState(false)
  const [closingResult, setClosingResult] = useState(null)

  useEffect(() => {
    fetchData()
  }, [page, search, activeTab])

  const fetchData = async () => {
    try {
      setLoading(true)
      if (activeTab === 'accounts') {
        const response = await savingsService.getByType('SUKARELA', {
          page,
          limit: 20,
          search
        })
        setAccounts(response.data.data.data || [])
        const meta = response.data.data.meta
        setTotalPages(meta?.totalPages || 1)
      } else if (activeTab === 'transactions') {
        const response = await savingsService.getTransactions({
          page,
          limit: 20,
          search
        })
        setTransactions(response.data.data.data || [])
        const meta = response.data.data.meta
        setTotalPages(meta?.totalPages || 1)
      } else if (activeTab === 'interest') {
        // Fetch interest rate settings
        const response = await savingsService.getInterestRates('SUKARELA')
        setInterestRates(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const handleMonthlyClosing = async () => {
    if (!confirm('Apakah Anda yakin ingin melakukan tutup buku bulanan? Bunga outstanding akan dipindahkan ke pokok simpanan.')) {
      return
    }

    setClosing(true)
    setClosingResult(null)
    try {
      const response = await savingsService.transferOutstandingInterest()
      setClosingResult(response.data.data)
      alert(`${response.data.message}`)
      fetchData()
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal melakukan tutup buku bulanan')
    } finally {
      setClosing(false)
    }
  }

  return (
    <div className="container-fluid">
      <div className="row mb-3">
        <div className="col-12">
          <h3><i className="fas fa-piggy-bank mr-2"></i>Simpanan Bunga Harian (SIBUHAR)</h3>
          <p className="text-muted">Simpanan sukarela dengan perhitungan bunga harian</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="row mb-3">
        <div className="col-12">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'accounts' ? 'active' : ''}`}
                onClick={() => setActiveTab('accounts')}
              >
                <i className="fas fa-list mr-1"></i> Detail Rekening
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'interest' ? 'active' : ''}`}
                onClick={() => setActiveTab('interest')}
              >
                <i className="fas fa-percentage mr-1"></i> Besaran Pembungaan
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'transactions' ? 'active' : ''}`}
                onClick={() => setActiveTab('transactions')}
              >
                <i className="fas fa-exchange-alt mr-1"></i> Detail Transaksi
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Search Bar */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Cari nomor rekening, nama anggota..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="input-group-append">
              <button className="btn btn-primary" onClick={fetchData}>
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>
        </div>
        <div className="col-md-6 text-right">
          <button
            className="btn btn-warning mr-2"
            onClick={handleMonthlyClosing}
            disabled={closing}
          >
            <i className="fas fa-calendar-check mr-1"></i> {closing ? 'Memproses...' : 'Tutup Buku Bulanan'}
          </button>
          <Link to="/savings" className="btn btn-success">
            <i className="fas fa-plus mr-1"></i> Buka Rekening Baru
          </Link>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-4">
          <i className="fas fa-spinner fa-spin fa-2x"></i>
          <p className="mt-2">Memuat data...</p>
        </div>
      ) : (
        <>
          {/* Detail Rekening Tab */}
          {activeTab === 'accounts' && (
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0"><i className="fas fa-list mr-2"></i>Detail Rekening SIBUHAR</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-striped table-hover mb-0">
                    <thead className="thead-dark">
                      <tr>
                        <th>No. Rekening</th>
                        <th>Nama Anggota</th>
                        <th>Saldo</th>
                        <th>Bunga/Tahun</th>
                        <th>Tgl Buka</th>
                        <th>Status</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">Tidak ada data rekening</td>
                        </tr>
                      ) : (
                        accounts.map((acc) => (
                          <tr key={acc.NoRekening}>
                            <td>
                              <Link to={`/savings/${acc.NoRekening}`} className="font-weight-bold">
                                {acc.NoRekening}
                              </Link>
                            </td>
                            <td>{acc.NamaAnggota}</td>
                            <td className="text-right font-weight-bold text-success">
                              {formatCurrency(acc.Saldo)}
                            </td>
                            <td>{acc.BungaPa ?? 6}%</td>
                            <td>{formatDate(acc.TglBuka)}</td>
                            <td>
                              <span className={`badge ${acc.Status === 'AKTIF' ? 'badge-success' : 'badge-danger'}`}>
                                {acc.Status}
                              </span>
                            </td>
                            <td>
                              <Link to={`/savings/${acc.NoRekening}`} className="btn btn-sm btn-info mr-1">
                                <i className="fas fa-eye"></i>
                              </Link>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Besaran Pembungaan Tab */}
          {activeTab === 'interest' && (
            <div className="card">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0"><i className="fas fa-percentage mr-2"></i>Besaran Pembungaan SIBUHAR</h5>
              </div>
              <div className="card-body">
                <div className="alert alert-warning">
                  <i className="fas fa-info-circle mr-2"></i>
                  Bunga dihitung harian berdasarkan saldo minimum dan dibayarkan bulanan.
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th>Tier Saldo</th>
                        <th>Minimal Saldo (Rp)</th>
                        <th>Maksimal Saldo (Rp)</th>
                        <th>Bunga per Tahun (%)</th>
                        <th>Keterangan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {interestRates.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            <div className="py-4">
                              <p>Data besaran pembungaan belum diatur</p>
                              <p className="text-muted">Default bunga: 6% per tahun</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        interestRates.map((rate, idx) => (
                          <tr key={idx}>
                            <td>Tier {idx + 1}</td>
                            <td className="text-right">{formatCurrency(rate.MinSaldo)}</td>
                            <td className="text-right">{formatCurrency(rate.MaxSaldo)}</td>
                            <td className="text-center font-weight-bold text-primary">{rate.BungaPa}%</td>
                            <td>{rate.Keterangan || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Detail Transaksi Tab */}
          {activeTab === 'transactions' && (
            <div className="card">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0"><i className="fas fa-exchange-alt mr-2"></i>Detail Transaksi SIBUHAR</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-striped table-hover mb-0">
                    <thead className="thead-dark">
                      <tr>
                        <th>Tanggal</th>
                        <th>No. Rekening</th>
                        <th>Jenis Transaksi</th>
                        <th className="text-right">Debit (Rp)</th>
                        <th className="text-right">Kredit (Rp)</th>
                        <th>Saldo</th>
                        <th>Keterangan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">Tidak ada data transaksi</td>
                        </tr>
                      ) : (
                        transactions.map((trx) => (
                          <tr key={trx.Id}>
                            <td>{formatDate(trx.TglTransaksi)}</td>
                            <td>
                              <Link to={`/savings/${trx.NoRekening}`}>
                                {trx.NoRekening}
                              </Link>
                            </td>
                            <td>
                              <span className={`badge ${
                                trx.JenisTransaksi === 'SETOR' ? 'badge-success' :
                                trx.JenisTransaksi === 'TARIK' ? 'badge-danger' :
                                trx.JenisTransaksi === 'BUNGA' ? 'badge-info' :
                                'badge-secondary'
                              }`}>
                                {trx.JenisTransaksi}
                              </span>
                            </td>
                            <td className="text-right text-success">
                              {trx.Debit > 0 ? formatCurrency(trx.Debit) : '-'}
                            </td>
                            <td className="text-right text-danger">
                              {trx.Kredit > 0 ? formatCurrency(trx.Kredit) : '-'}
                            </td>
                            <td className="text-right font-weight-bold">
                              {formatCurrency(trx.Saldo)}
                            </td>
                            <td>{trx.Keterangan || '-'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="row mt-3">
              <div className="col-12 d-flex justify-content-center">
                <nav>
                  <ul className="pagination">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPage(page - 1)}>
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                      <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => setPage(i + 1)}>
                          {i + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPage(page + 1)}>
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default SimpananBungaHarian
